<template>
  <div id="cash-log-main-list">
    <div class="list body" :class="{ 'free-flow': !pending }" ref="listRef">
      <table>
        <thead>
          <tr>
            <th class="id">{{ $t('ID') }}</th>
            <th class="date">{{ $t('DATE') }}</th>
            <th class="amount">{{ $t('CASH_IN_OUT') }}</th>
            <th class="type">{{ $t('CASH_LOG_TYPE') }}</th>
            <th class="category">{{ $t('CASH_LOG_CATEGORY') }}</th>
            <th class="branch">{{ $t('BRANCH') }}</th>
            <th class="loan-id">{{ $t('CNTRCT_ID') }}</th>
            <th class="renew-loan-id">{{ $t('RENEW_CNTRCT_ID') }}</th>
            <th class="notes">{{ $t('NOTES') }}</th>
            <th class="agent">{{ $t('CASH_LOG_AGENT') }}</th>
            <th class="fund">{{ $t('FUND') }}</th>
            <template  v-if="!pending">
              <th class="cash-balance">{{ $t('CASH_BALANCE') }}</th>
              <th class="bank-balance" v-if="isHeadAccountant">{{ $t('BANK_BALANCE') }}</th>
            </template>
            <th class="more"></th>
          </tr>
        </thead>
        <tbody :class="{ 'free-flow': !pending }" ref="listRef">
          <tr :key="item.id" v-for="item in items" ref="itemsRef">
            <td class="id">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('ID') }}:</div>
              <div>{{ item.id }}</div>
            </td>
            <td class="date">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('DATE') }}:</div>
              <div>{{ moment(item.registrationDate, DATE_FORMAT).format('DD/MM/YYYY') }}</div>
            </td>
            <td
              class="amount"
              :class="{
                out: (item.direction < 0),
                in: (item.direction > 0)
              }"
            >
              <div class="mobile-header" v-if="!isDesktop">{{ $t('CASH_IN_OUT') }}:</div>
              <div>{{ (item.direction > 0) ? addCommas(item.amount) : `(${addCommas(item.amount)})` }}</div>
            </td>
            <td class="type">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('CASH_LOG_TYPE') }}:</div>
              <div>{{ $t(item.type) }}</div>
            </td>
            <td class="category">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('CASH_LOG_CATEGORY') }}:</div>
              <div>{{ item.category ? $t(item.category.key) : '' }}</div>
            </td>
            <td class="branch">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('BRANCH') }}:</div>
              <div>{{ item.targetBranch ? item.targetBranch.name : '' }}</div>
            </td>
            <td class="loan-id">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('CNTRCT_ID') }}:</div>
              <div>{{ item.mifosLoanId || (item.contract ? item.contract.publicId : '') || '' }}</div>
            </td>
            <td class="renew-loan-id">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('RENEW_CNTRCT_ID') }}:</div>
              <div>{{ item.mifosRenewLoanId || ((item.contract && item.contract.renewal) ? item.contract.renewal.publicId : '') || '' }}</div>
            </td>
            <td class="notes">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('NOTES') }}:</div>
              <div>{{ item.notes }}</div>
            </td>
            <td class="agent">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('CASH_LOG_AGENT') }}:</div>
              <div>{{ item.agent ? `${item.agent.firstName} ${item.agent.lastName}` : '' }}</div>
            </td>
            <td class="fund">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('FUND') }}:</div>
              <div>{{ item.fund.name }}</div>
            </td>
            <template v-if="!pending">
              <td class="cash-balance">
                <div class="mobile-header" v-if="!isDesktop">{{ $t('CASH_BALANCE') }}:</div>
                <div>{{ treatNegative(addCommas(item.cashBalance)) }}</div>
              </td>
              <td class="bank-balance" v-if="isHeadAccountant">
                <div class="mobile-header" v-if="!isDesktop">{{ $t('BANK_BALANCE') }}:</div>
                <div>{{ treatNegative(addCommas(item.bankBalance)) }}</div>
              </td>
            </template>
            <td class="more">
              <div class="mobile-header" v-if="!isDesktop">{{ $t('ACTIONS') }}:</div>
              <action-menu :item="item" @action-made="() => getItems()" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination
      v-if="pending"
      :page="filters.page"
      :pageSize="filters.pageSize"
      :total="total"
      @pageSizeChange="changePageSize"
      @pageChange="changePage"
    />
  </div>
</template>

<script>
import { computed, watch, onUnmounted, ref } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import moment from 'moment';
import $ from 'jquery';

import Pagination from '@/components/Pagination';

import { DATE_FORMAT, PERMISSIONS } from '@/consts';
import { addCommas } from '@/helpers';
import { hasPermissions } from '@/helpers/permission';

import ActionMenu from './ActionMenu';

const { useState, useActions } = createNamespacedHelpers('cashLog');
const { useState: useAppState } = createNamespacedHelpers('app');

export default {
  components: {
    ActionMenu,
    Pagination
  },
  props: {
    pending: Boolean
  },
  setup(props) {
    const { isDesktop } = useAppState(['isDesktop']);

    const { items, filters, total, isLoading } = useState(['items', 'filters', 'total', 'isLoading']);
    const {
      getCashLogList,
      getCashLogPendingList,
      applyFilter,
      reset
    } = useActions([
      'getCashLogList',
      'getCashLogPendingList',
      'applyFilter',
      'reset'
    ]);

    const pending = computed(() => props.pending);
    const isHeadAccountant = computed(() => hasPermissions([PERMISSIONS.HEAD_ACCOUNTANT_ACCESS]));
    const listRef = ref(null);
    const itemsRef = ref(null);

    const changePage = (value) => {
      applyFilter({
        page: value,
      });
    };

    const getItems = async (passiveFiltering) => {
      if (props.pending) {
        getCashLogPendingList();
      } else {
        getCashLogList(passiveFiltering);
      }
    };

    const changePageSize = (value) => {
      applyFilter({
        pageSize: value,
        page: 1,
      });
    };

    const treatNegative = (str) => ((str[0] === '-') ? `(${str.substring(1, str.length)})` : str);

    watch(pending, (val) => {
      applyFilter({
        page: 1,
        pageSize: val ? 20 : 200
      });

      getItems();
    }, {
      immediate: true
    });

    watch(filters, (f, lastF) => {
      const passiveFiltering = ((f.fromDate !== lastF.fromDate) || (f.toDate !== lastF.toDate) || (f.page !== lastF.page));
      getItems(passiveFiltering);
    });

    watch(itemsRef, () => {
      if (pending.value) return;
      if (!listRef.value || !itemsRef.value) return;

      if (filters.value.page === 1) {
        listRef.value.scrollTop = listRef.value.scrollHeight;
      }
    });

    watch(listRef, () => {
      if (pending.value) return;
      if (!listRef.value) return;

      const list = $(listRef.value);

      list.on('scroll', (e) => {
        const h = list.height();
        const { scrollTop } = e.target;

        if (scrollTop < h * 2) {
          if (isLoading.value) return;
          if (Math.ceil(total.value / filters.value.pageSize) <= filters.value.page) return;

          applyFilter({
            page: filters.value.page + 1
          });
        }
      });
    });

    onUnmounted(() => {
      reset();
    });

    return {
      items,
      moment,
      DATE_FORMAT,
      getItems,
      addCommas,
      filters,
      total,
      changePageSize,
      changePage,
      isHeadAccountant,
      treatNegative,
      listRef,
      itemsRef,
      isDesktop
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#cash-log-main-list {
  .list {
    &.body {
      margin-bottom: 20px;
    }

    &.free-flow {
      max-height: calc(100vh - 345px);
      overflow-y: auto;

      &.body {
        margin-bottom: 0;
      }
    }

    table {
      width: 100%;
      border-collapse: inherit;
      border-spacing: 0;

      thead {
        text-transform: uppercase;
        font-weight: bold;
        text-align: center;

        tr {
          background-color: $babyPowder;

          th {
            padding: 15px 10px;
            border-top: 1px solid $alto;
            border-bottom: 1px solid $alto;
            position: sticky;
            top: 0;
            z-index:9;
            background-color: $babyPowder;

            &:first-child {
              border-top-left-radius: 3px;
              border-bottom-left-radius: 3px;
              border-left: 1px solid $alto;

            }

            &:last-child {
              border-top-right-radius: 3px;
              border-bottom-right-radius: 3px;
              border-right: 1px solid $alto;
            }
          }
        }
      }

      tbody {
        text-align: center;

        tr {
          background-color: $babyPowder;
          cursor: default;

          td {
            padding: 15px 5px;
            border-top: 1px solid $alto;
            border-bottom: 1px solid $alto;

            &:first-child {
              border-top-left-radius: 3px;
              border-bottom-left-radius: 3px;
              border-left: 1px solid $alto;
              color: $geranium;
            }

            &:last-child {
              border-top-right-radius: 3px;
              border-bottom-right-radius: 3px;
              border-right: 1px solid $alto;
            }

            &.amount {
              &.in {
                color: $blueNcs;
              }

              &.out {
                color: $brightMaroon;
              }
            }
          }
        }
      }
    }

  @media (max-width: 991px) {

    table {
      width: auto;
      display: block;

      thead {
        display: block;

        tr {
          position: absolute;
          top: -9999px;
          left: -9999px;
        }
      }

      tbody {
        text-align: start;
        display: block;

        tr {
          display: block;

          &:last-child {
            border-bottom: 1px solid $alto;
          }

          td {
            padding: 2px;
            display:block;
            border: none;
            position: relative;
            padding-left: 45%;
            padding-right: 6px;
            min-height: 30px;

            &:first-child {
              border-left: unset;
              border-top: 1px solid $alto;
              padding-top: 20px;
              border-top-left-radius: unset;
            }

            &:last-child {
              border-right: unset;
              padding-bottom: 20px;
              padding-top: 5px;
            }

            &::before {
              position: absolute;
              top: 0;
              left: 6px;
              width: 45%;
              padding-right: 10px;
              white-space: nowrap;
              text-align: center;
            }

            .mobile-header {
              position: absolute;
              left: 6px;
              padding-right: 10px;
              white-space: nowrap;
              text-align: center;
              padding-left: 10px;
            }
          }
        }
      }
    }
  }
  }
}
</style>
